import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { AlertTriangle, Car, Clock, ExternalLink, Camera, Trash2, Upload, X } from 'lucide-react';
import { mockParkingData } from '../data/mockData';
import EnforcementView from './EnforcementView';
import ConsumerView from './ConsumerView';
import BrandingSettings, { DEFAULT_BRANDING, hexToRgba } from './BrandingSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const safeSaveLocations = (locations) => {
  try {
    localStorage.setItem('parkingFakeLocations', JSON.stringify(locations));
    return true;
  } catch {
    return false;
  }
};

const ParkingEnforcementApp = ({ projectSlug, initialBranding, initialLocations, initialDestinations, onBackToDashboard, token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('consumer');
  const [parkingData, setParkingData] = useState(null);
  const [consumerData, setConsumerData] = useState(null);
  const [fakeLocations, setFakeLocations] = useState([]);
  const [destinations, setDestinations] = useState(initialDestinations || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedAttribute, setSelectedAttribute] = useState('all');
  const [violationThreshold, setViolationThreshold] = useState(1);
  const [citations, setCitations] = useState({});
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [citationNotes, setCitationNotes] = useState('');
  const [jsonInput, setJsonInput] = useState('');
  const [consumerJsonInput, setConsumerJsonInput] = useState('');
  const [citationPhoto, setCitationPhoto] = useState(null);
  const { toast } = useToast();

  // Branding state - load from prop or localStorage
  const [branding, setBranding] = useState(() => {
    if (initialBranding) return { ...DEFAULT_BRANDING, ...initialBranding };
    try {
      const saved = localStorage.getItem('parkingBranding');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (!parsed.textMode) parsed.textMode = 'light';
        if (parsed.mapCenter && 
            Math.abs(parsed.mapCenter.lat - 26.7153) < 0.001 && 
            Math.abs(parsed.mapCenter.lng - (-80.0534)) < 0.001) {
          parsed.mapCenter = null;
        }
        localStorage.setItem('parkingBranding', JSON.stringify(parsed));
        return { ...DEFAULT_BRANDING, ...parsed };
      }
      return DEFAULT_BRANDING;
    } catch {
      return DEFAULT_BRANDING;
    }
  });

  // Load fake locations from prop or localStorage
  useEffect(() => {
    if (initialLocations && initialLocations.length > 0) {
      setFakeLocations(initialLocations);
      return;
    }
    const geocodeAddress = async (addr) => {
      const strategies = [
        addr,
        addr.replace(/^\d+\s+/, ''),
        addr.replace(/^[\d\s]+/, '').replace(/^,\s*/, ''),
      ];
      const parts = addr.split(',').map(p => p.trim()).filter(Boolean);
      if (parts.length >= 2) {
        strategies.push(parts.slice(1).join(', '));
        strategies.push(parts[parts.length - 2] + ', ' + parts[parts.length - 1]);
      }
      if (parts.length >= 1) {
        strategies.push(parts[parts.length - 1]);
      }

      for (const query of strategies) {
        if (!query || query.length < 3) continue;
        try {
          const encoded = encodeURIComponent(query);
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encoded}&limit=1`,
            { headers: { 'User-Agent': 'ParkingManagementApp/1.0' } }
          );
          const results = await response.json();
          if (results && results.length > 0) {
            return { lat: parseFloat(results[0].lat), lng: parseFloat(results[0].lon) };
          }
          await new Promise(r => setTimeout(r, 300));
        } catch {
          // continue to next strategy
        }
      }
      return null;
    };

    const loadAndFix = async () => {
      try {
        const saved = localStorage.getItem('parkingFakeLocations');
        if (!saved) return;
        
        const locations = JSON.parse(saved);
        let needsUpdate = false;

        // Re-geocode any locations missing coordinates
        for (let i = 0; i < locations.length; i++) {
          if (!locations[i].coordinates && locations[i].address1) {
            const coords = await geocodeAddress(locations[i].address1);
            if (coords) {
              locations[i].coordinates = coords;
              needsUpdate = true;
            }
            // Small delay to respect Nominatim rate limits
            await new Promise(r => setTimeout(r, 1100));
          }
        }

        if (needsUpdate) {
          safeSaveLocations(locations);
        }
        setFakeLocations(locations);
      } catch {
        // ignore
      }
    };

    loadAndFix();
  }, []);

  const handleBrandingChange = (newBranding) => {
    setBranding(newBranding);
  };

  const handleAddFakeLocation = (location) => {
    setFakeLocations(prev => {
      const updated = [...prev, location];
      if (!safeSaveLocations(updated)) {
        toast({ title: "Storage Full", description: "Try using a smaller photo or removing old locations.", variant: "destructive" });
      }
      return updated;
    });
    toast({
      title: "Location Added",
      description: `${location.displayName} has been added`,
    });
  };

  const handleRemoveFakeLocation = (locationId) => {
    setFakeLocations(prev => {
      const updated = prev.filter(loc => loc.id !== locationId);
      safeSaveLocations(updated);
      return updated;
    });
    toast({
      title: "Location Removed",
      description: "Custom location has been removed",
    });
  };

  const handleClearFakeLocations = () => {
    setFakeLocations([]);
    localStorage.removeItem('parkingFakeLocations');
    toast({
      title: "Cleared",
      description: "All custom locations have been removed",
    });
  };

  const handleUpdateFakeLocation = (updatedLocation) => {
    setFakeLocations(prev => {
      const updated = prev.map(loc => loc.id === updatedLocation.id ? updatedLocation : loc);
      if (!safeSaveLocations(updated)) {
        toast({ title: "Storage Full", description: "Try using a smaller photo or removing old locations.", variant: "destructive" });
      }
      return updated;
    });
    toast({
      title: "Location Updated",
      description: `${updatedLocation.displayName} has been updated`,
    });
  };

  const handleAddDestination = (dest) => {
    setDestinations(prev => [...prev, dest]);
    toast({ title: "Destination Added", description: `${dest.name} has been added` });
  };

  const handleRemoveDestination = (destId) => {
    setDestinations(prev => prev.filter(d => d.id !== destId));
    toast({ title: "Destination Removed", description: "Destination has been removed" });
  };

  const handleUpdateDestination = (updatedDest) => {
    setDestinations(prev => prev.map(d => d.id === updatedDest.id ? updatedDest : d));
    toast({ title: "Destination Updated", description: `${updatedDest.name} has been updated` });
  };

  const processedSpaces = useMemo(() => {
    if (!parkingData) return [];
    
    return parkingData.data.site.parkingSpaces.nodes
      .filter(space => {
        const occupancyStatus = space.parkingSpaceDataPoints.nodes[0]?.occupancyStatus;
        return occupancyStatus === 2;
      })
      .map(space => {
        const dataPoint = space.parkingSpaceDataPoints.nodes[0];
        const vehicleSession = space.parkingSpaceVehicleSessions.nodes[0];
        const vehicleInfo = vehicleSession?.parkingSpaceVehicleSessionBestAttribute;
        
        const startTime = new Date(dataPoint.period.start.value);
        const currentTime = new Date();
        const hoursParked = Math.floor((currentTime - startTime) / (1000 * 60 * 60));
        const minutesParked = Math.floor(((currentTime - startTime) % (1000 * 60 * 60)) / (1000 * 60));
        
        const isViolation = hoursParked >= violationThreshold;
        
        return {
          id: space.id,
          name: space.name,
          level: space.siteLevel.displayName,
          parkingAttribute: dataPoint.parkingAttribute.displayName || 'Regular',
          plate: vehicleInfo?.vehicleRecognitionPlate?.plate || 'N/A',
          make: vehicleInfo?.vehicleRecognitionMakeModel?.make || 'Unknown',
          model: vehicleInfo?.vehicleRecognitionMakeModel?.model || 'Unknown',
          hoursParked,
          minutesParked,
          isViolation,
          startTime,
          isCited: citations[space.id] || false
        };
      });
  }, [parkingData, violationThreshold, citations]);

  const filteredSpaces = useMemo(() => {
    return processedSpaces.filter(space => {
      const matchesSearch = searchTerm === '' || 
        space.plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
        space.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
        space.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        space.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesLevel = selectedLevel === 'all' || space.level === selectedLevel;
      const matchesAttribute = selectedAttribute === 'all' || space.parkingAttribute === selectedAttribute;
      
      return matchesSearch && matchesLevel && matchesAttribute;
    });
  }, [processedSpaces, searchTerm, selectedLevel, selectedAttribute]);

  const stats = useMemo(() => {
    const totalVehicles = processedSpaces.length;
    const violations = processedSpaces.filter(space => space.isViolation).length;
    const citationCount = Object.values(citations).filter(Boolean).length;
    
    return { totalVehicles, violations, citations: citationCount };
  }, [processedSpaces, citations]);

  const levels = useMemo(() => {
    if (!processedSpaces.length) return [];
    return [...new Set(processedSpaces.map(space => space.level))].sort();
  }, [processedSpaces]);

  const attributes = useMemo(() => {
    if (!processedSpaces.length) return [];
    return [...new Set(processedSpaces.map(space => space.parkingAttribute))].sort();
  }, [processedSpaces]);

  const handleImportConsumerJSON = (directData = null) => {
    if (directData) {
      console.log("Consumer data imported (direct):", directData);
      setConsumerData(directData);
      try {
        setConsumerJsonInput(JSON.stringify(directData, null, 2));
      } catch (stringifyError) {
        console.warn("Could not stringify directData, but data was set:", stringifyError);
        setConsumerJsonInput("Data imported successfully");
      }
      toast({
        title: "Success",
        description: "Consumer data imported successfully",
      });
      return;
    }
    
    if (!consumerJsonInput.trim()) {
      toast({
        title: "Error",
        description: "Please paste consumer JSON data first",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const data = JSON.parse(consumerJsonInput);
      console.log("Consumer data imported (from input):", data);
      setConsumerData(data);
      toast({
        title: "Success",
        description: "Consumer data imported successfully",
      });
    } catch (error) {
      console.error("Consumer JSON import error:", error);
      toast({
        title: "Error",
        description: "Invalid JSON format",
        variant: "destructive",
      });
    }
  };

  const handleImportJSON = () => {
    if (!jsonInput.trim()) {
      toast({
        title: "Error",
        description: "Please paste JSON data first",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const data = JSON.parse(jsonInput);
      setParkingData(data);
      toast({
        title: "Success",
        description: "JSON data imported successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Invalid JSON format",
        variant: "destructive",
      });
    }
  };

  const loadMockData = () => {
    setParkingData(mockParkingData);
    toast({
      title: "Success", 
      description: "Mock data loaded successfully",
    });
  };

  const clearData = () => {
    switch (activeTab) {
      case 'enforcement':
        setParkingData(null);
        setJsonInput('');
        setCitations({});
        setSearchTerm('');
        setSelectedLevel('all');
        setSelectedAttribute('all');
        toast({
          title: "Success",
          description: "Enforcement data cleared successfully",
        });
        break;
        
      case 'consumer':
        setConsumerData(null);
        setConsumerJsonInput('');
        toast({
          title: "Success",
          description: "Find Parking data cleared successfully", 
        });
        break;
        
      default:
        setParkingData(null);
        setConsumerData(null);
        setCitations({});
        setJsonInput('');
        setConsumerJsonInput('');
        setSearchTerm('');
        setSelectedLevel('all');
        setSelectedAttribute('all');
        toast({
          title: "Success",
          description: "All data cleared successfully",
        });
    }
  };

  const handleIssueCitation = (spaceId) => {
    setCitations(prev => ({
      ...prev,
      [spaceId]: true
    }));
    setSelectedSpace(null);
    setCitationNotes('');
    setCitationPhoto(null);
    toast({
      title: "Citation Issued",
      description: `Citation issued for space ${processedSpaces.find(s => s.id === spaceId)?.name}`,
    });
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setCitationPhoto(e.target.result);
        };
        reader.readAsDataURL(file);
        toast({
          title: "Photo Added",
          description: "Citation photo uploaded successfully",
        });
      } else {
        toast({
          title: "Error",
          description: "Please select an image file",
          variant: "destructive",
        });
      }
    }
  };

  const handleRemovePhoto = () => {
    setCitationPhoto(null);
    toast({
      title: "Photo Removed",
      description: "Citation photo removed",
    });
  };

  const formatDuration = (hours, minutes) => {
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  };

  // Dynamic title based on branding
  const appTitle = branding.organizationName || 'Parking Management System';
  const primaryColor = branding.primaryColor || '#3b82f6';
  const accentColor = branding.accentColor || '#f97316';
  const isDarkText = branding.textMode === 'dark';
  const textColor = isDarkText ? '#1e293b' : '#ffffff';
  const textSubColor = isDarkText ? 'rgba(30,41,59,0.6)' : 'rgba(255,255,255,0.7)';
  const btnBorderColor = isDarkText ? 'rgba(30,41,59,0.3)' : 'rgba(255,255,255,0.4)';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Branded Header Bar */}
      <div 
        className="w-full shadow-lg"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {branding.logoUrl && (
                <img
                  src={branding.logoUrl}
                  alt="Organization logo"
                  className="h-16 w-auto object-contain"
                  style={{}}
                />
              )}
              <div>
                <h1 
                  className="text-3xl font-bold tracking-tight"
                  style={{ color: textColor }}
                >
                  {appTitle}
                </h1>
                <p style={{ color: textSubColor }} className="text-sm mt-0.5">
                  {branding.organizationName ? 'Parking Availability System' : 'Real-time parking availability'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {onBackToDashboard && (
                <Button
                  onClick={onBackToDashboard}
                  variant="outline"
                  className="flex items-center space-x-2"
                  style={{ color: textColor, borderColor: btnBorderColor, backgroundColor: 'transparent' }}
                  data-testid="back-to-dashboard-btn"
                >
                  <span>Projects</span>
                </Button>
              )}
              <BrandingSettings
                branding={branding}
                onBrandingChange={handleBrandingChange}
              />
              <Button 
                onClick={clearData}
                variant="outline"
                className="flex items-center space-x-2"
                style={{ 
                  color: textColor, 
                  borderColor: btnBorderColor,
                  backgroundColor: 'transparent',
                }}
              >
                <Trash2 className="w-4 h-4" />
                <span>Clear Data</span>
              </Button>
            </div>
          </div>
        </div>
        {/* Accent stripe */}
        <div 
          className="h-1"
          style={{ backgroundColor: accentColor }}
        />
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="consumer" className="flex items-center space-x-2">
                <Car className="w-4 h-4" />
                <span>Find Parking</span>
              </TabsTrigger>
              <TabsTrigger value="enforcement" className="flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4" />
                <span>Enforcement</span>
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="consumer" className="mt-6">
              <ConsumerView 
                consumerData={consumerData}
                consumerJsonInput={consumerJsonInput}
                setConsumerJsonInput={setConsumerJsonInput}
                handleImportConsumerJSON={handleImportConsumerJSON}
                fakeLocations={fakeLocations}
                onAddFakeLocation={handleAddFakeLocation}
                onRemoveFakeLocation={handleRemoveFakeLocation}
                onUpdateFakeLocation={handleUpdateFakeLocation}
                onClearFakeLocations={handleClearFakeLocations}
                branding={branding}
                projectSlug={projectSlug}
                onBrandingChange={handleBrandingChange}
                token={token}
                destinations={destinations}
                onAddDestination={handleAddDestination}
                onRemoveDestination={handleRemoveDestination}
                onUpdateDestination={handleUpdateDestination}
              />
            </TabsContent>

            <TabsContent value="enforcement" className="mt-6">
              <EnforcementView 
                parkingData={parkingData}
                jsonInput={jsonInput}
                setJsonInput={setJsonInput}
                handleImportJSON={handleImportJSON}
                loadMockData={loadMockData}
                processedSpaces={processedSpaces}
                filteredSpaces={filteredSpaces}
                stats={stats}
                levels={levels}
                attributes={attributes}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedLevel={selectedLevel}
                setSelectedLevel={setSelectedLevel}
                selectedAttribute={selectedAttribute}
                setSelectedAttribute={setSelectedAttribute}
                violationThreshold={violationThreshold}
                setViolationThreshold={setViolationThreshold}
                citations={citations}
                selectedSpace={selectedSpace}
                setSelectedSpace={setSelectedSpace}
                citationNotes={citationNotes}
                setCitationNotes={setCitationNotes}
                citationPhoto={citationPhoto}
                handlePhotoUpload={handlePhotoUpload}
                handleRemovePhoto={handleRemovePhoto}
                handleIssueCitation={handleIssueCitation}
                formatDuration={formatDuration}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ParkingEnforcementApp;
